import { toastSuccess } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import useEditor from "shared/hooks/useEditor"
import { CategoryId } from "shared/hubrise/ids"
import { IProduct } from "shared/hubrise/models"
import * as Category from "shared/hubrise/utils/Category"

import { Actions, ActionText, ActionLink, Container } from "./Styles"

export const useBulkTaxUpdateLabel = (product: IProduct, onBulkUpdate: (categoryId: CategoryId | null) => number) => {
  const { t } = useTranslation()
  const { catalog } = useEditor()

  const [showBulkActions, setShowBulkActions] = React.useState(false)

  const category = React.useMemo(() => Category.byId(catalog, product.categoryId), [catalog, product.categoryId])

  const allProductsCount = catalog.data.products.length - 1 // Exclude current product

  const categoryProductsCount = React.useMemo(
    () => catalog.data.products.filter((p) => p.categoryId === category.id && p.id !== product.id).length,
    [catalog.data.products, category, product.id],
  )

  const label = React.useMemo(() => {
    if (!showBulkActions) {
      return t("product.tax.label")
    }

    const applyToAllProducts = () => {
      const count = onBulkUpdate(null)
      setShowBulkActions(false)
      toastSuccess(t("product.tax.bulk_update.all_products_updated", { count }))
    }

    const applyToCategoryProducts = () => {
      const count = onBulkUpdate(category.id)
      setShowBulkActions(false)
      toastSuccess(t("product.tax.bulk_update.category_products_updated", { count, categoryName: category.name }))
    }

    return (
      <Container>
        <span>{t("product.tax.label")}</span>

        <Actions>
          <ActionText>{t("product.tax.bulk_update.apply_to")}:</ActionText>
          {allProductsCount > 0 && (
            <ActionLink onClick={applyToAllProducts}>
              {t("product.tax.bulk_update.all_products", { count: allProductsCount })}
            </ActionLink>
          )}
          {categoryProductsCount > 0 && (
            <ActionLink onClick={applyToCategoryProducts}>
              {t("product.tax.bulk_update.category_products", {
                categoryName: category.name,
                count: categoryProductsCount,
              })}
            </ActionLink>
          )}
        </Actions>
      </Container>
    )
  }, [showBulkActions, t, allProductsCount, categoryProductsCount, category, onBulkUpdate])

  return {
    label: label as string,
    onChange: () => setShowBulkActions(true),
  }
}
